<template>
  <div>
    <details-modal
      :show="show"
      :current-operation="currentOperation"
      :loading="loading"
      @close="closeModal"
      @cancel="closeModal"
      @create="createLetterbox"
      @update="updateLetterbox"
      @delete="deleteLetterbox"
    >
      <div v-show="loading">
        <Loader />
      </div>
      <div v-show="!loading">
        <Form :validation-schema="validationSchema" validate-on-mount>
          <div class="form-group">
            <label class="control-label">*Nom</label>
            <Field
              name="name"
              v-model="letterbox.name"
              v-slot="{ field, errorMessage }"
            >
              <input v-bind="field" class="form-control" type="text" />
              <small v-show="errorMessage" class="text-danger">
                Le nom est requis.
              </small>
            </Field>
          </div>
          <p>
            <label>*Type</label>
            <el-select
              id="select-owners"
              v-model="letterbox.type"
              class="select-info col-md-12 px-1 select-one-line-per-item"
              size="large"
              placeholder="Type"
              filterable
            >
              <el-option key="generique" label="Générique" value="GENERIQUE" />
              <el-option
                key="reservable"
                label="Réservable"
                value="RESERVABLE"
              />
            </el-select>

            <Field
              name="type"
              v-model="letterbox.type"
              v-slot="{ errorMessage }"
            >
              <small v-show="errorMessage" class="text-danger">
                Le type est requis.
              </small>
            </Field>
          </p>
          <div>
            <label class="control-label">Accès</label>
            <div v-for="(access, index) in letterbox.access" :key="index">
              <UserAccess
                :accessRight="access.accessRight"
                :collaborator="access.collaborator"
                :position="index"
                :collaboratorsName="collaboratorsName"
                @collaboratorChange="collaboratorChange"
                @accessChange="accessChange"
                @deleteAccess="deleteAccess"
              />
            </div>
            <div>
              <p-button type="light" round @click="addNewAccess">
                Ajouter
              </p-button>
            </div>
          </div>
          <p><label>* champs obligatoires</label></p>
        </Form>
        <p v-if="errorMessage" class="error-message-in-modal">
          {{ errorMessage }}
        </p>
      </div>
    </details-modal>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import { Form, Field } from 'vee-validate';
import * as yup from 'yup';
import MixinFetchData from '@/mixins/mixinFetchData';
import DetailsModal from './DetailsModal.vue';
import UserAccess from '../UIComponents/UserAccess.vue';
import Loader from '../UIComponents/Loader.vue';

export default {
  components: {
    DetailsModal,
    UserAccess,
    Loader,
    Form,
    Field,
  },
  mixins: [MixinFetchData],
  props: ['show', 'currentOperation', 'letterboxProps'],
  data() {
    return {
      letterbox: {
        name: null,
        type: null,
        access: [
          {
            accessRight: '',
            collaborator: null,
          },
        ],
      },
      errorMessage: '',
      validationSchema: {
        name: yup.string().required(),
        type: yup.string().required(),
      },
      loading: false,
    };
  },
  watch: {
    show: {
      handler(newVal) {
        if (newVal) {
          this.fetchData();
        }
      },
    },
    letterboxProps: {
      handler(newVal) {
        if (newVal) {
          this.letterbox = {
            ...newVal,
            access: newVal.access.map((access) => ({ ...access })),
          };
        }
      },
    },
  },
  methods: {
    async createLetterbox() {
      // Validate each field with vee-validate, and post the new letterbox.
      const validationSchemaObject = yup.object(this.validationSchema);
      const isLetterboxValid = await validationSchemaObject.isValid(
        this.letterbox,
      );

      if (isLetterboxValid) {
        this.errorMessage = null;
        this.loading = false;
        this.$http
          .post(`${process.env.VUE_APP_INTRANET_API_URL}/commands/letterbox/`, {
            name: this.letterbox.name,
            type: this.letterbox.type,
            access: this.letterbox.access.map((access) => ({
              collaboratorId: access.collaborator.id,
              accessRight: access.accessRight,
            })),
          })
          .then((response) => {
            this.closeModal();
          })
          .catch((error) => {
            this.errorMessage = 'Une erreur est survenue lors de la création de la boîte aux lettres, veuillez réessayer ou contacter le webmaster.';
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        this.errorMessage = 'Un ou plusieurs champs sont invalides.';
      }
    },
    async updateLetterbox() {
      const validationSchemaObject = yup.object(this.validationSchema);
      const isLetterboxValid = await validationSchemaObject.isValid(
        this.letterbox,
      );

      if (isLetterboxValid) {
        this.errorMessage = null;

        this.loading = true;

        this.$http
          .patch(
            `${process.env.VUE_APP_INTRANET_API_URL}/commands/letterbox/${this.letterbox.id}`,
            {
              name: this.letterbox.name,
              type: this.letterbox.type,
              access: this.letterbox.access.map((access) => ({
                id: access.id,
                collaboratorId: access.collaborator.id,
                accessRight: access.accessRight,
              })),
            },
          )
          .then(() => {
            this.closeModal();
          })
          .catch((error) => {
            this.errorMessage = 'Une erreur est survenue lors de la modification de la boîte aux lettres, veuillez réessayer ou contacter le webmaster.';
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        this.errorMessage = 'Un ou plusieurs champs sont invalides.';
      }
    },
    deleteLetterbox() {
      this.errorMessage = null;

      Swal.fire({
        title: 'Êtes-vous sûr de vouloir supprimer cette boîte aux lettres ?',
        text: "Il ne sera pas possible d'annuler cette suppression !",
        icon: 'warning',
        showCancelButton: true,
        customClass: {
          confirmButton: 'btn btn-success btn-fill',
          cancelButton: 'btn btn-danger btn-fill',
        },
        confirmButtonText: 'Oui, effacer',
        cancelButtonText: 'Non, conserver',
        buttonsStyling: false,
      })
        .then((result) => {
          if (result.isConfirmed) {
            this.$http
              .delete(
                `${process.env.VUE_APP_INTRANET_API_URL}/commands/letterbox/${this.letterbox.id}`,
              )
              .then(() => {
                this.closeModal();
              })
              .catch((error) => {
                this.errorMessage = 'Une erreur est survenue lors de la suppression du véhicule, veuillez réessayer ou contacter le webmaster.';
              });
          }
        })
        .catch(() => {});
    },
    closeModal() {
      this.$emit('close');
      this.resetData();
      this.errorMessage = '';
    },
    resetData() {
      this.letterbox = {
        name: null,
        type: null,
        access: [
          {
            accessRight: '',
            collaborator: null,
          },
        ],
      };
    },
    fetchData() {
      this.loading = true;
      // These functions are coming from the "mixinFetchData" mixin.
      this.fetchCollaboratorsNames().then(() => {
        this.loading = false;
      });
    },
    accessChange({ value, position }) {
      const { access } = this.letterbox;
      access[position].accessRight = value;

      this.letterbox.access = access;
    },
    collaboratorChange({ value, position }) {
      const { access } = this.letterbox;
      access[position].collaborator = value;

      this.letterbox.access = access;
    },
    addNewAccess() {
      const { access } = this.letterbox;
      access.push({
        accessRight: '',
        collaborator: null,
      });

      this.letterbox.access = access;
    },
    deleteAccess(position) {
      const { access } = this.letterbox;
      access.splice(position, 1);

      this.letterbox.access = access;
    },
  },
};
</script>
